import React, { useCallback } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  AlignSelf,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Color,
  Display,
  FlexDirection,
  Grid,
  Heading1,
  JustifyContent,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CopyConfirmation } from 'SocialLinks/CopyConfirmation'
import { appendNativeShareUtm, SHARE_SURFACES } from 'SocialLinks/shareNatively'
import { getMessage, SHARE_TITLE } from 'SocialLinks/socialReferralHelper'
import { useShareWithFallback } from 'SocialLinks/useShareWithFallback'

import { RAFLinkShare } from '../RAFLinkShare'
import type { IRAFOffer } from '../ReferralPresentation'
import { RAFInstructions } from './RAFInstructions'
import { RafFriendGetsDiscounts } from './RafFriendGetsDiscounts'
import { RafYouGet } from './RafYouGet'

const RelativeBox = styled(Box)`
  position: relative;
  margin-bottom: -25px;
  margin-top: 40px;
`

const StickyMobileShareButton = styled(Box)`
  padding: 16px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 768px) {
    display: none;
  }
`

const FullWidthButton = styled(Button)`
  padding: 8px 16px;
  width: 100%;
`

const PageCol = styled(Col)`
  @media (min-width: 1024px) {
    margin-left: 32px;
    margin-right: 32px;
  }
`

const RafSharingContainer = withCitrusTheme(Box, (theme) => ({
  background: theme.color(Color.White),
  width: '100%',
  padding: '32px 62px',
  borderRadius: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  boxShadow: '2px 4px 8px 0px #0000000f',
  [`@media (max-width: 767px)`]: {
    display: 'none',
  },
  marginTop: '24px',
}))

const Divider = withCitrusTheme(Box, (theme) => ({
  borderColor: theme.color(Color.ColdGrey_800),
  borderLeftWidth: 0.5,
  borderTopWidth: 0.5,
  borderStyle: BorderStyle.Dashed,
}))

type RafSharingProps = {
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: (payload: { target: string }) => void
  trackingReferFriend: (actionType: string, actionName: string) => void
  device: string
  userRAFLink: string
  userFirstName: string
  offer: IRAFOffer
  offerTitle: string
}

export const RafSharing: React.FC<RafSharingProps> = ({
  trackingReferFriendSocialSharing,
  trackUserFreeFoodLinkShare,
  trackingReferFriend,
  device,
  userRAFLink,
  userFirstName,
  offer,
  offerTitle,
}) => {
  const firstBoxDiscount = offer.get('firstBoxDiscountFormatted') as string
  const firstMonthDiscount = offer.get('firstMonthDiscountFormatted') as string
  const secondBoxDiscount = offer.get('secondBoxDiscountFormatted') as string
  const credit = offer.get('creditFormatted') as string
  const discountDuration = offer.getIn(['discountDuration', 'formatted']) as string

  const { isCopied, share } = useShareWithFallback({
    copyText: userRAFLink,
    surface: SHARE_SURFACES.rafPage,
    trackingReferFriend,
    trackUserFreeFoodLinkShare,
  })

  const onMobileShare = useCallback(() => {
    share({
      title: SHARE_TITLE,
      url: appendNativeShareUtm(userRAFLink),
      text: getMessage(offer),
    })
  }, [userRAFLink, offer, share])

  return (
    <RelativeBox>
      <Grid
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        flexDirection={FlexDirection.Row}
      >
        <PageCol
          size={[12, 8, 8, 8]}
          gridColumn={['1 / span 12', '1 / span 12', '3 / span 8', '1 / span 12']}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
        >
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.SpaceEvenly}
            gap={6}
            minWidth="75%"
          >
            <Box gap={1} display={Display.Flex} flexDirection={FlexDirection.Column}>
              <Heading1 textAlign={TextAlign.Center} color={Color.ColdGrey_800}>
                {offerTitle}
              </Heading1>
            </Box>
            <Box
              display="flex"
              flexDirection={[
                FlexDirection.Column,
                FlexDirection.Column,
                FlexDirection.Row,
                FlexDirection.Row,
              ]}
              alignItems={[
                AlignItems.Center,
                AlignItems.Center,
                AlignItems.Center,
                AlignItems.Center,
              ]}
              justifyContent={[
                JustifyContent.Center,
                JustifyContent.Center,
                JustifyContent.SpaceBetween,
                JustifyContent.SpaceBetween,
              ]}
              gap={[8, 8, 4, 4]}
              paddingBottom={4}
              style={{ width: '100%' }}
            >
              <RafYouGet credit={credit} />
              <Divider
                alignSelf={[
                  AlignSelf.Center,
                  AlignSelf.Center,
                  AlignSelf.Stretch,
                  AlignSelf.Stretch,
                ]}
                width={['280px', '280px', '1px', '1px']}
              />
              <RafFriendGetsDiscounts
                firstBoxDiscount={firstBoxDiscount}
                secondBoxDiscount={secondBoxDiscount}
                firstMonthDiscount={firstMonthDiscount}
                discountDuration={discountDuration}
              />
            </Box>
          </Box>
          <RafSharingContainer justifyContent={JustifyContent.Center}>
            <Box minWidth={['100%', '100%', '100%', '75%']}>
              <RAFLinkShare
                userRAFLink={userRAFLink}
                userFirstName={userFirstName}
                trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
                device={device}
                trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
                trackingReferFriend={trackingReferFriend}
                isBoost={false}
                credit={credit}
              />
            </Box>
          </RafSharingContainer>
          <RAFInstructions offer={offer} />
        </PageCol>
      </Grid>
      <StickyMobileShareButton>
        <FullWidthButton
          colorVariant={ButtonColorVariant.Primary}
          onClick={onMobileShare}
          width="100%"
        >
          Share your link
        </FullWidthButton>
        {isCopied && <CopyConfirmation credit={credit} />}
      </StickyMobileShareButton>
    </RelativeBox>
  )
}
